import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, Tooltip, IconButton, Icon, FormControlLabel, Checkbox } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation, Link } from 'react-router-dom';

import Loader from '../../common/loader';
import Crud from '../../common/crud';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import Trash from '../../common/trash';

import AuthContext from '../../context';
import { del, get, post, put } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const EmployeeHourAction = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        data: [],
        eha: {
            idemployee_hour_action: 0,
            name: '',
            nm_payment: true
        },
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Employee hour action';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/employeeHourAction');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, data: response?.data ?? [] }));
        } else {
            console.log('[EmployeeHourAction] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChange = ({ target }) => setState(prev => ({ ...prev, eha: { ...prev.eha, [target.name]: target.value } }));

    const handleCheck = ({ target }) => setState(prev => ({ ...prev, eha: { ...prev.eha, [target.name]: target.checked } }));

    const validateForm = (action) => {
        const { idemployee_hour_action, name } = state.eha;

        switch (action) {
            case 'update':
                const data = state.data.filter(value => value.idemployee_hour_action === idemployee_hour_action);

                if (data[0]) {
                    const before = JSON.stringify(data[0]);
                    const after = JSON.stringify(state.eha);
                    if (before !== after) {
                        if (name.trim()) {
                            return true;
                        } else {
                            showToast('All fields is required', 'error');
                            return false;
                        }
                    } else {
                        showToast('Update some field', 'error');
                        return false;
                    }
                } else {
                    showToast('Select some data', 'error');
                    return false;
                }

            case 'store':

                if (name.trim()) {
                    return true;
                } else {
                    showToast('All fields is required', 'error');
                    return false;
                }

            case 'delete':
                if (idemployee_hour_action === 0) {
                    showToast('Select some data', 'error');
                    return false;
                }
                const cond = window.confirm('Are you sure you want to delete this data?');

                return Boolean(idemployee_hour_action && cond);
            default:
                break;
        }
    }

    const clear = () => {
        setState(prev => ({ ...prev, eha: { ...prev.eha, idemployee_hour_action: 0, name: '', nm_payment: true } }));
    }

    const store = async () => {
        const cond = validateForm('store');

        if (cond) {
            showLoader(true);
            const response = await post('/employeeHourAction/store', state.eha);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? prev.data }));
                showToast(response.message);
            } else {
                console.log('[EmployeeHourAction] store error', response);
                helpers.failRequest(response, showToast, signOut);
                showLoader(false);

                return false
            }
            showLoader(false);
        }

        return cond;
    }

    const update = async () => {
        const { idemployee_hour_action } = state.eha;
        const cond = validateForm('update');

        if (cond) {
            showLoader(true);
            const response = await put(`/employeeHourAction/${idemployee_hour_action}/update`, state.eha);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? prev.data }));
                showToast(response.message);
            } else {
                console.log('[EmployeeHourAction] update error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const destroy = async () => {
        const { idemployee_hour_action } = state.eha;
        const cond = validateForm('delete');

        if (cond) {
            showLoader(true);
            const response = await del(`/employeeHourAction/${idemployee_hour_action}`);
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response?.data ?? prev.data }));
                showToast(response.message);
            } else {
                console.log('[EmployeeHourAction] destroy error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const valueGetter = ({ value }) => value ? '✓' : '✗';

    const renderCell = ({ row }) => {
        return (
            <Link to={`/history/employee_hour_action/${row.idemployee_hour_action}`} target='_blank'>
                <Tooltip title='Show history' placement='top'>
                    <IconButton>
                        <Icon>{'history'}</Icon>
                    </IconButton>
                </Tooltip>
            </Link>
        );
    }

    const columns = [
        { field: 'name', headerName: 'Name', flex: 1 },
        { field: 'nm_payment', headerName: 'NM payment', flex: 0.7, valueGetter },
        { field: 'actions', headerName: 'Actions', flex: 0.3, renderCell }
    ];

    if (state.loading) {
        return <Loader />;
    }
    if (!state.permissions.read_attr) {
        return <NotFound />;
    }

    return (
        <div className={classes.root}>
            <ViewTitle title='Employee hour action' />
            <div>
                <Grid container>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormLabel>{'Name'}</FormLabel>
                            <FormControl
                                variant='filled'
                                component={Box}
                                width='100%'
                            >
                                <FilledInput
                                    autoComplete='off'
                                    type='text'
                                    placeholder='Name'
                                    name='name'
                                    value={state.eha.name}
                                    onChange={handleChange}
                                />
                            </FormControl>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            checked={state.eha.nm_payment}
                            control={<Checkbox color='primary' />}
                            label='NM payment'
                            labelPlacement='top'
                            name='nm_payment'
                            onChange={handleCheck}
                        />
                    </Grid>
                </Grid>
            </div>

            <Crud
                permissions={state.permissions}
                create={{ create: clear, store }}
                update={update}
                destroy={destroy}
                cancel={clear}
            />

            {
                state.permissions.delete_attr &&
                <Trash table='employee_hour_action' id='idemployee_hour_action' onRefresh={makeRequest} />
            }

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idemployee_hour_action}
                    onRowClick={({ row }) => setState(prev => ({ ...prev, eha: row }))}
                />
            </div>
        </div>
    );
}

export default EmployeeHourAction;